// src/modules/app-views/_apps/appmulti/plans/pages/Document/tabs/RecordTab/modals/PricesTableModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Row, Col, Button, InputNumber, Spin } from 'antd';
import { MinusCircleOutlined, PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import plansService from 'modules/app-views/_apps/appmulti/plans/service';
import axiosService from 'services/axiosService';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PricesTableModal = ({ open, onClose, documentId, refresh, currency, locked }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const currencySymbol = plansService.getCurrencySymbol(currency || 'USD');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    // Load existing priceTable from API (backup parity: reads from document)
    (async () => {
      if (!documentId) { form.setFieldsValue({ priceTable: [] }); return; }
      try {
        setLoading(true);
        const [doc] = await axiosService.get({ url: `/v1/plans/${documentId}`, returnAsObject: true });
        const existing = doc?.priceTable || [];
        form.setFieldsValue({ priceTable: existing });
      } catch {
        form.setFieldsValue({ priceTable: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, documentId, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOk = useCallback(async () => {
    if (locked) { onClose(); return; }
    try {
      setLoading(true);
      const values = await form.validateFields();
      const rows = (values.priceTable || []).map(row => ({
        from: row.from ?? 0,
        to: row.to ?? 0,
        value: row.value ?? 0,
        currency: currency || 'USD',
      }));
      // Save directly to API (backup parity)
      await axiosService.put({
        url: `/v1/plans/${documentId}`,
        _id: documentId,
        data: { priceType: 'table', priceTable: rows },
      });
      onClose();
      refresh?.();
    } catch {
      // validation errors shown inline
    } finally {
      setLoading(false);
    }
  }, [form, documentId, onClose, locked, currency, refresh]);

  const handleCancel = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UnorderedListOutlined />
          {translateX('table_price')}
        </span>
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnHidden
      width={520}
      okText={translateX(locked ? 'close' : 'save')}
      okType={locked ? 'default' : 'primary'}
      cancelButtonProps={{ style: { display: locked ? 'none' : 'inline' } }}
      cancelText={translateX('cancel')}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={16} style={{ marginBottom: 8 }}>
          <Col span={7}>
            <span className="text-muted" style={{ fontSize: 12 }}>
              {translateX('from')}
            </span>
          </Col>
          <Col span={7}>
            <span className="text-muted" style={{ fontSize: 12 }}>
              {translateX('to')}
            </span>
          </Col>
          <Col span={8}>
            <span className="text-muted" style={{ fontSize: 12 }}>
              {translateX('value')}
            </span>
          </Col>
        </Row>

        <Form.List name="priceTable">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key} align="middle">
                  <Col span={7}>
                    <Form.Item
                      {...restField}
                      name={[name, 'from']}
                      rules={[{ required: true, message: '' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <InputNumber min={1} max={999} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item
                      {...restField}
                      name={[name, 'to']}
                      rules={[{ required: true, message: '' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <InputNumber min={1} max={999} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      rules={[{ required: true, message: '' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <InputNumber
                        min={0.01}
                        max={9999.99}
                        precision={2}
                        prefix={currencySymbol}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                      style={{ marginBottom: 8 }}
                      disabled={fields.length <= 1}
                    />
                  </Col>
                </Row>
              ))}

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  {translateX('daily_m_p')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PricesTableModal;
